import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { TextInput, Button } from '../common/form';
import classnames from 'classnames';
import css from 'styled-jsx/css';
import { toast } from 'react-toastify';
import { Contact } from '../../api';
import Joi from '@hapi/joi';

const { className: pageContentContainerClassName, styles: pageContentContainerStyleOverrides } = css.resolve`
  .container.page_content_container {
    background-color: white;
    position: relative;
    bottom: 50px;
    padding-top: 100px;
    padding: 100px 60px 80px 60px;
  }
  @media (max-width: 576px) {
    .container.page_content_container {
      background-color: white;
      position: relative;
      bottom: 0px;
      padding-top: 100px;
      padding: 20px 20px 20px 20px;
    }
  }
`;

const withFormValidation = (WrappedComponent, config = {}) => {
  return class Validator extends React.Component {
    constructor(props) {
      super(props);

      const { fields } = config;
      const fieldNames = Object.keys(fields);

      const initialState = {};
      for (let fieldName of fieldNames) {
        const field = fields[fieldName];
        let defaultValue = field.defaultValue;
        if (defaultValue === undefined) {
          defaultValue = '';
        }
        initialState[fieldName] = defaultValue;
        initialState[`${fieldName}Error`] = '';

        if (field.placeholder) {
          initialState[`${fieldName}Placeholder`] = field.placeholder;
        }
      }

      this.state = initialState;
    }

    resolveDependentFieldValues = (currentValue, fields) => {
      return fields.reduce((acc, field) => {
        acc[field.name] = field.resolver(currentValue);
        return acc;
      }, {});
    };

    setFieldValue = (fieldName, newValue) => {
      this.setState((prevState) => {
        const { fields } = config;
        const field = fields[fieldName];
        const dependentFieldChanges = this.resolveDependentFieldValues(newValue, field.dependentFields);
        const updates = {
          ...dependentFieldChanges,
          [fieldName]: newValue,
        };

        const nextState = { ...prevState };
        for (let key in updates) {
          nextState[key] = newValue;
        }

        return nextState;
      });
    };

    resetFields = () => {
      const { fields } = config;
      const fieldNames = Object.keys(fields);
      const nextState = {};
      for (let fieldName of fieldNames) {
        const field = fields[fieldName];
        let defaultValue = field.defaultValue;
        if (defaultValue === undefined) {
          defaultValue = '';
        }

        nextState[fieldName] = defaultValue;
        nextState[`${fieldName}Error`] = '';
      }

      this.setState(nextState);
    };

    doValidation = (fieldName) => {
      let errorMessage = '';
      const { fields } = config;
      const field = fields[fieldName];
      if (!field) return errorMessage;

      const currentValue = this.state[fieldName];
      const validationResult = field.validationSchema.validate(currentValue);
      if (validationResult.error) {
        errorMessage = field.validationErrorMessage;
      }

      return errorMessage;
    };

    validateField = (fieldName) => {
      const errorMessage = this.doValidation(fieldName);
      this.setState((prevState) => {
        const nextState = { ...prevState };
        nextState[`${fieldName}Error`] = errorMessage;
        return nextState;
      });
    };

    validateFields = (fieldNames) => {
      const fieldErrorMessages = fieldNames.map(this.doValidation);
      this.setState((prevState) => {
        const nextState = { ...prevState };
        fieldNames.forEach((fieldName, index) => {
          nextState[`${fieldName}Error`] = fieldErrorMessages[index];
        });

        return nextState;
      });

      return fieldErrorMessages.every((errorMessage) => errorMessage.length === 0);
    };

    render() {
      const { setFieldValue, validateField, validateFields, resetFields } = this;
      const actions = {
        setFieldValue,
        validateField,
        validateFields,
        resetFields,
      };

      return <WrappedComponent {...this.state} {...actions} {...this.props} />;
    }
  };
};

class ContactForm extends React.Component {
  constructor(props) {
    super(props);
  }

  validateForm = () => {
    const { validateFields, resetFields, handleValidationSuccess, name, email, subject, message } = this.props;

    const fieldsToValidate = ['name', 'email', 'subject', 'message'];
    const formDataIsValid = validateFields(fieldsToValidate);
    if (formDataIsValid) {
      const formData = { name, email, subject, message };
      handleValidationSuccess(formData, resetFields);
    }
  };

  render() {
    const {
      name,
      namePlaceholder,
      nameError,
      email,
      emailPlaceholder,
      emailError,
      subject,
      subjectPlaceholder,
      subjectError,
      message,
      messagePlaceholder,
      messageError,
      setFieldValue,
      validateField,
      submittingForm,
    } = this.props;
    return (
      <div className="contact_form">
        <TextInput
          hasShadow
          size="large"
          value={name}
          placeholder={namePlaceholder}
          error={nameError}
          onChange={(newValue) => {
            setFieldValue('name', newValue);
          }}
          onBlur={() => {
            validateField('name');
          }}
          styleOverrides={{
            marginBottom: 15,
          }}
        />
        <TextInput
          hasShadow
          size="large"
          value={email}
          placeholder={emailPlaceholder}
          error={emailError}
          onChange={(newValue) => {
            setFieldValue('email', newValue);
          }}
          onBlur={() => {
            validateField('email');
          }}
          styleOverrides={{
            marginBottom: 15,
          }}
        />
        <TextInput
          hasShadow
          size="large"
          value={subject}
          placeholder={subjectPlaceholder}
          error={subjectError}
          onChange={(newValue) => {
            setFieldValue('subject', newValue);
          }}
          onBlur={() => {
            validateField('subject');
          }}
          styleOverrides={{
            marginBottom: 15,
          }}
        />

        <div
          className={classnames('textarea_container', {
            error: !!messageError,
          })}
        >
          <textarea
            onChange={(e) => {
              setFieldValue('message', e.target.value);
            }}
            onBlur={() => {
              validateField('message');
            }}
            placeholder={messagePlaceholder}
            value={message}
            rows="5"
          ></textarea>
          {messageError && <p className="textarea_error">{messageError}</p>}
        </div>

        <Button size="large" onClick={this.validateForm} showSpinner={submittingForm} disabled={submittingForm}>
          <span>Send Message</span>
        </Button>

        <style jsx>{`
          .textarea_container {
            width: 100%;
            margin-bottom: 15px;
          }
          .textarea_container textarea {
            width: 100%;
            border: 1px solid #e1e5ee;
            border-radius: 4px;
            box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.02);
            background-color: white;
            padding: 15px;
          }
          .textarea_container.error textarea {
            border-color: #e01e40;
          }
          textarea::placeholder {
            color: #aab0be;
          }
          .textarea_error {
            color: #e01e40 !important;
          }
        `}</style>
      </div>
    );
  }
}

const formConfig = {
  fields: {
    name: {
      name: 'name',
      validationSchema: Joi.string().min(1),
      validationErrorMessage: 'Please enter your name',
      dependentFields: [],
      placeholder: 'Your Full Name',
    },
    email: {
      name: 'email',
      validationSchema: Joi.string().email({ tlds: false }),
      validationErrorMessage: 'Please enter a valid email',
      dependentFields: [],
      placeholder: 'Your Email Address',
    },
    subject: {
      name: 'subject',
      validationSchema: Joi.string().min(6),
      validationErrorMessage: 'Enter at least 6 characters',
      dependentFields: [],
      placeholder: 'Message Subject',
    },
    message: {
      name: 'message',
      validationSchema: Joi.string().min(6),
      validationErrorMessage: 'Enter at least 6 characters',
      dependentFields: [],
      placeholder: 'How can we help?',
    },
  },
};

const ContactFormWithValidation = withFormValidation(ContactForm, formConfig);

class ContactPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      submittingForm: false,
    };
  }

  submitMessage = async (data, onSuccess) => {
    this.setState({ submittingForm: true });
    const result = await Contact.sendMessage(data);
    this.setState({ submittingForm: false });

    if (result instanceof Error) {
      toast(result.message, {
        className: 'error-toast',
      });
    } else {
      onSuccess();
      toast('Your message was sent!', {
        className: 'success-toast',
        position: 'bottom-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  handleContactFormValidationSuccess = (data, onSuccessfulSubmission) => {
    this.submitMessage(data, onSuccessfulSubmission);
  };

  render() {
    return (
      <div className="page contact">
        <div className="section hero">
          <Container fluid>
            <Row>
              <Col style={{ paddingLeft: 0, paddingRight: 0 }}>
                <div className="hero_content_wrapper">
                  <div className="hero_content">
                    {/* <h5 className="intro">SDKs & Integrations</h5> */}
                    <h1 className="headline">SDKs & Integrations</h1>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        <div className="section page_content">
          <Container className={`${pageContentContainerClassName} page_content_container`}>
            <Row>
              <Col style={{ paddingLeft: 0, paddingRight: 0 }}>
                <div className="content_column">
                  {/* <div className="content_section">
                    <h4 className="section_header">Complaints?</h4>
                    <p className="section_text">
                      We’re really sorry you’re not happy. We want to sort things out for you quickly, so please give us
                      a ring on{' '}
                      <a class="" href="tel:+4403332029614" data-di-id="di-id-78eeaebe-2650fc0d">
                        (+233) 55 649 5905
                      </a>
                      . (Lines are open 24/7). If you’d rather complain in writing, please email us at{' '}
                      <a href="mailto:info@expresspaygh.com">info@expresspaygh.com</a>.
                    </p>
                  </div> */}

                  <div className="content_section">
                    <h4 className="section_header">Looking to Integrate?</h4>
                    <p className="section_text">
                      Looking to start accepting payments with expressPay? Our Merchant team is standing by to
                      assist. Message us at{' '}
                      <a href="mailto:integrations@expresspaygh.com">merchant@expresspaygh.com</a> or call{' '}
                      <a class="" href="tel:+4403332029614" data-di-id="di-id-78eeaebe-2650fc0d">
                        (+233) 30 700 0503
                      </a>{' '}
                      for assistance
                    </p>
                  </div>

                  {/* <div className="content_section">
                    <p className="section_text">
                      For any other questions, feedback, feature requests, please leave us a message below, or contact{' '}
                      <a href="mailto:integrations@expresspaygh.com">integrations@expresspaygh.com</a> or call{' '}
                      <a class="" href="tel:+4403332029614" data-di-id="di-id-78eeaebe-2650fc0d">
                        (+233) 55 649 5905
                      </a>{' '}
                      for assistance
                    </p>
                  </div> */}

                  <div id="contact-form">
                    <div className="content_section">
                      {/* <h4 className="section_header">Leave us a Message</h4> */}
                      <p className="section_text">Alternatively, send us a message using this form</p>
                    </div>
                    <ContactFormWithValidation
                      submittingForm={this.state.submittingForm}
                      handleValidationSuccess={this.handleContactFormValidationSuccess}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {pageContentContainerStyleOverrides}
        <style jsx>{`
          .hero {
            background-color: #4a4a4a;
            text-align: center;
            color: white;
            font-family: 'Open Sans';
          }
          .hero {
            background-color: #4a4a4a;
            background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
              url(./img/developer-background@2x.png);
            // background-position: 0% 48%;
            background-repeat: no-repeat;
            background-size: cover;
            text-align: center;
            color: white;
            font-family: 'Open Sans';
          }
          .hero_content_wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 451px;
          }
          @media (max-width: 576px) {
            // .hero_content_wrapper {
            //   height: 351px;
            // }
          }
          @media (max-width: 576px) {
            .hero {
              // background-size: 151%;
              background-position: center center;
            }
            .hero_content_wrapper {
              height: 311px;
            }
          }
          .hero_content {
            width: 35%;
          }
          @media (max-width: 991px) {
            .hero_content {
              width: 65%;
            }
          }
          @media (max-width: 576px) {
            .hero_content {
              width: 90%;
              margin-top: 60px;
            }
          }
          .hero .intro {
            color: #ffffff;
            font-size: 16px;
            font-weight: 600;
            line-height: 10px;
            text-align: center;
            text-transform: uppercase;
            margin-bottom: 20px;
          }
          .hero .headline {
            font-size: 34px;
            font-weight: bold;
            line-height: 46px;
            text-align: center;
          }
          @media (max-width: 991px) {
            .hero .headline {
              font-size: 24px;
              line-height: 33px;
            }
            .hero .intro {
              font-size: 15px;
              line-height: 10px;
              margin-bottom: 15px;
            }
          }
          .page_content {
            background-color: white;
          }
          .page_content .content_column {
            width: 70%;
            margin-left: auto;
            margin-right: auto;
          }
          @media (max-width: 576px) {
            .page_content .content_column {
              width: 90%;
            }
          }
          .page_content .content_section {
            margin-bottom: 40px;
          }
          .page_content .section_header {
            color: #3a3a3a;
            font-size: 22px;
            font-weight: 600;
            line-height: 35px;
          }
          .page_content .section_text {
            color: #3a3a3a;
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0.67px;
            line-height: 28px;
          }
        `}</style>
      </div>
    );
  }
}

export default ContactPage;
